// Optimized version of DataBrowser with improved UI using Ant Design v5 & Material UI v7

import { Modal as MuiModal } from '@mui/material';
import * as Sentry from '@sentry/react';
import { Spin, Tabs } from 'antd';
import { getCurrentUser } from 'aws-amplify/auth';
import SentryWrap from "components/Sentry";
import React, { useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

// Constants and UI

// Thunks
import { useCurrentOrg } from "store/organizations/selectors";
import { handleGetDistrictsWithCount } from "store/organizations/thunks";
// Tables
import SchoologyBookMapByCourse from "components/admin/bookmaps/schoology_district_course";
import ClassesTable from "containers/SuperAdmin/Section/ClassTable";
import CoursesTable from "containers/SuperAdmin/Section/CourseTable";
import DistrictsAdminTable from "containers/SuperAdmin/Section/DistrictAdminList";
// import DistrictsTable from "features/forms/admin/DistrictsTable";
import DistrictsTable from "containers/SuperAdmin/Section/DistrictTable";
import ParentTable from "containers/SuperAdmin/Section/ParentTable";
import SchoolTable from "containers/SuperAdmin/Section/SchoolTable";
import StudentsTable from "containers/SuperAdmin/Section/StudentTable";
import TeacherTable from "containers/SuperAdmin/Section/TeacherTable";
import queryString from 'query-string';

const tab_key = {
    teacher:"see_teachers",
    see_teachers:"see_teachers",
    student:"see_students",
    see_students:"see_students",
    parent:"parent",
    see_schools:"see_schools",
    school:"see_schools",
    district_admins:"district_admins",
    admin:"district_admins",
    see_districts:"see_districts",
    see_classes:"see_classes",
    see_courses:"see_courses",
    schoology_courses:'schoology_courses'
}

const LoadingModal = ({ visible }) => (
    <MuiModal open={visible}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <Spin size="large" />
        </div>
    </MuiModal>
);

const DataBrowser = ({districts, schools, users , classes , distStudents}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const currentOrg = useCurrentOrg();
    const [activeTab, setActiveTab] = useState("see_districts");
    const [userloading, setUserLoading] = useState(true);
    const [loading, setLoading] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const search = queryString.parse(location.search);
        if (search && search.tab) {
            if (tab_key[search.tab]) {
                if(activeTab!=tab_key[search.tab])
                setActiveTab(tab_key[search.tab])
            }
            else {
                setActiveTab("see_districts");
            }
        } else {
            setActiveTab("see_districts");
        }
    }, [location.search])

    const tabItems = [
        {
            key: "see_districts",
            label: "Districts",
            children: <DistrictsTable
                districts={districts}
                userloading={userloading}
                onTabsJump={handleTabJump}
                active_tab={activeTab}
            />
        },
        {
            key: "see_schools",
            label: "Schools",
            children: <SchoolTable
                userloading={userloading}
                onTabsJump={handleTabJump}
                active_tab={activeTab}
            />
        },
        {
            key: "see_teachers",
            label: "Teachers",
            children: <TeacherTable
                userloading={userloading}
                onTabsJump={handleTabJump}
                active_tab={activeTab}
            />
        },
        { key: "see_students", 
            label: "Students", 
            children: <StudentsTable 
            userloading={userloading} 
            onTabsJump={handleTabJump}
            active_tab={activeTab}
             /> },
        {
            key: "parent",
            label: "Parents",
            children: <ParentTable
                userloading={userloading}
                onTabsJump={handleTabJump}
                 active_tab={activeTab}
            />
        },
        {
            key: "district_admins",
            label: "District Admins",
            children: <DistrictsAdminTable
                users={users}
                userloading={userloading}
                 active_tab={activeTab}
            />
        },
        {
            key: "see_classes",
            label: "Classes",
            children: <ClassesTable
                users={users}
                schools={schools}
                classes={classes}
                userloading={userloading}
                onTabsJump={handleTabJump}
                 active_tab={activeTab}
            />
        },
        {
            key: "see_courses",
            label: "Courses",
            children: currentOrg?.canvas_id && <CoursesTable
                users={users}
                userloading={userloading}
                schools={[...schools, currentOrg]}
                setUserLoading={setUserLoading}
                 active_tab={activeTab}
            />
        },
        {
            key: "schoology_courses",
            label: "Course Mapping",
            children: currentOrg?.schoolId && <SchoologyBookMapByCourse district_id={currentOrg.id} />
        },
    ].filter(tab => tab.children);

    function handleTabJump(tabKey, distData) {
        setActiveTab(tabKey);
        setTimeout(()=>{
             navigate(`?tab=${tab_key[tabKey]||''}`);
        },500)
    }

    async function fetchDistrictData() {
        try {
            setUserLoading(true);
            const district_loaded = districts && districts.length
            setLoading(!district_loaded);
            await dispatch(handleGetDistrictsWithCount())
        } catch (error) {
            Sentry.captureException(`Error in fetchDistrictData: ${error}`);
        } finally {
            setUserLoading(false);
            setLoading(false);
        }
    }

    useEffect(() => {
        getCurrentUser().catch(() => navigate("/"));
    }, []);

    useEffect(() => {
        fetchDistrictData();
    }, []);
    const onSwitchTab = (key) => {
        setActiveTab(key)
        setTimeout(() => {
            navigate(`?tab=${tab_key[key] || ''}`);
        }, 500)
    }
    return (
        <SentryWrap>
            <LoadingModal visible={loading} />
            <Tabs
                activeKey={activeTab}
                onChange={onSwitchTab}
                type="line"
                items={tabItems}
                style={{ padding: '20px' }}
            />
        </SentryWrap>
    );
};

const mapStateToProps = (state) => ({
    classes: state.classes,
    districts: state.districts,
    schools: state.schools,
    users: state.users,
    booksAllList: state.booksAllList,
    allUser: state.allUser,
    allSchools: state.allSchools,
    distStudents: state.distStudents,
});

export default connect(mapStateToProps)(DataBrowser);